import React from "react";
import styled from "styled-components";
import { useSortBy, useTable } from "react-table";
import PropTypes from "prop-types";
import { Col, Row, Table } from "reactstrap";
import { PaginationListStandalone } from "react-bootstrap-table2-paginator";
import SizePerPageDropdown from "../SizePerPageDropdown/SizePerPageDropdown";
import { AvForm } from "availity-reactstrap-validation";

const defaultPropGetter = () => ({});

const ReactTableForm = ({
  loading,
  data,
  columns,
  onTableChange,
  pagination,
  firstIsSticky,
  isSearch,
  withFooter,
  isStriped = false,
  overflow = true,
  firstCellIsMultiple = false,
  className,
  customColorsCashback = false,
  getCellProps = defaultPropGetter,
  childNoData
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: data?.results || data,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useSortBy
  );

  const [page, setPage] = React.useState(1);
  const [sizePerPage, setSizePerPage] = React.useState(10);
  const [customHeaderGroups, setCustomHeaderGroups] = React.useState(null);
  const [search, setSearch] = React.useState(data?.search || "");
  const [tableHeight, setTableHeight] = React.useState();
  const [tableSortType, setTableSortType] = React.useState({
    ordering: "none",
    current_ordering: "none",
  });
  const tableRef = React.useRef();

  React.useEffect(() => {
    setPage(data.page);
    setSizePerPage(data.sizePerPage || data.size);
    return () => {
      setPage(1);
      setSizePerPage(10);
      setCustomHeaderGroups(null);
    };
  }, []);

  React.useEffect(() => {
    if (headerGroups[1] !== undefined) {
      let headersArray = {
        headers: [],
        getHeaderGroupProps: headerGroups[1]?.getHeaderGroupProps,
        getFooterGroupProps: headerGroups[1]?.getFooterGroupProps,
      };

      let subHeadersArray = {
        headers: [],
        getHeaderGroupProps: headerGroups[0].getHeaderGroupProps,
        getFooterGroupProps: headerGroups[0].getFooterGroupProps,
      };

      let headers = headerGroups[0];
      let subHeaders = headerGroups[1];

      subHeaders.headers.map(i => {
        if (i.depth > 0) {
          subHeadersArray.headers.push(i);
        } else {
          headersArray.headers.push(i);
        }
      });

      headers.headers.map(i => {
        if (i.depth !== undefined) {
          headersArray.headers.push(i);
        }
      });

      setCustomHeaderGroups([headersArray, subHeadersArray]);
    } else {
      let headersArray = {
        headers: [],
        getHeaderGroupProps: headerGroups[0].getHeaderGroupProps,
        getFooterGroupProps: headerGroups[0].getFooterGroupProps,
      };

      let headers = headerGroups[0];

      headers.headers.map(i => {
        headersArray.headers.push(i);
      });

      setCustomHeaderGroups([headersArray]);
    }
  }, [columns]);

  const paginationSettings = {
    alwaysShowAllBtns: true,
    custom: true,
    dataSize: parseInt(data.count),
    firstPageText: "<<",
    firstPageTitle: "First page",
    hidePageListOnlyOnePage: false,
    hideSizePerPage: false,
    lastPageText: ">>",
    lastPageTitle: "Last page",
    nextPageText: ">",
    nextPageTitle: "Next page",
    onPageChange: val => handleChangeTable("page", val),
    page: parseInt(data.page),
    pageStartIndex: 1,
    paginationSize: 5,
    prePageText: "<",
    prePageTitle: "Previous page",
    sizePerPage: sizePerPage,
    totalSize: data.count,
    withFirstAndLast: true,
  };

  const handleChangeTable = (type, value) => {
    let tableHeight = tableRef?.current?.clientHeight;
    setTableHeight(tableHeight || 0);

    const prev = {
      page,
      sizePerPage,
      search,
    };

    if (!isSearch) {
      delete prev.search;
    }

    if (type === "sort") {
      if (tableSortType.current_ordering === value?.src) {
        switch (tableSortType.ordering) {
          case "none":
            setTableSortType({
              ordering: "asc",
              current_ordering: "-" + value?.src,
            });
            if (onTableChange) {
              return onTableChange({
                ...prev,
                ordering: value?.src,
              });
            }
          case "asc":
            setTableSortType({
              ordering: "desc",
              current_ordering: value?.src,
            });
            if (onTableChange) {
              return onTableChange({
                ...prev,
                ordering: value?.src,
              });
            }
            return void 0;
          case "desc":
            setTableSortType({ ordering: "none", current_ordering: "none" });
            if (onTableChange) {
              return onTableChange({
                ...prev,
                ordering: "",
              });
            }
            return void 0;
        }
      } else {
        setTableSortType({
          ordering: "asc",
          current_ordering: value?.src,
        });
        if (onTableChange) {
          return onTableChange({
            ...prev,
            ordering: "-" + value?.src,
          });
        }
        return void 0;
      }
    }

    if (type === "size") {
      setSizePerPage(value);
      if (onTableChange) {
        onTableChange({
          ...prev,
          sizePerPage: value,
        });
      }
      return void 0;
    }

    if (type === "page") {
      setPage(value);
      if (onTableChange) {
        onTableChange({
          ...prev,
          page: value,
        });
      }
      return void 0;
    }

    if (type === "search") {
      setSearch(value);
      if (onTableChange) {
        onTableChange({
          ...prev,
          search: value,
        });
      }
      return void 0;
    }
  };

  const spinnerTable = (
    <div className="loader-for-react-table">
      <div className="spinner-border text-primary m-1" role="status">
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  );

  const mergeCell = (i, k, sw, cell) => {
    switch (sw) {
      case "col":
        if (i == 0 && k == 1) {
          return "3";
        } else if (i == 0 && k == 4) {
          return "2";
        }
      case "display":
        if (i == 0 && k == 2) {
          return "d-none";
        } else if (i == 0 && k == 5) {
          return "d-none";
        } else if (i == 0 && k == 3) {
          return "d-none";
        }
      default:
        return "";
    }
  };

  return (
    <React.Fragment>
      {!!isSearch && (
        <Row>
          <Col md={4}>
            <div className="search-box me-2 mb-2 d-inline-block">
              <div className="position-relative">
                <label htmlFor="search-bar-0" className="search-label">
                  <span id="search-bar-0-label" className="sr-only">
                    Search this table
                  </span>
                  <input
                    id="search-bar-0"
                    type="text"
                    aria-labelledby="search-bar-0-label"
                    className="form-control "
                    placeholder="Search"
                    value={search}
                    onChange={e => handleChangeTable("search", e.target.value)}
                  />
                </label>
                <i className="bx bx-search-alt search-icon" />
              </div>
            </div>
          </Col>
        </Row>
      )}
      <Styles
        ref={tableRef}
        className={`position-relative ${className}`} //fade-in-custom-animate 
        style={{
          overflow: overflow ? "auto" : "",
        }}
      >
        {loading && (
          <div
            className="fade-in-custom-animate w-100 h-100 d-flex align-items-center justify-content-center spin-loader opacity-20 absolute z-[9999] bg-[#ffffff4d]"
            style={{ 
              position: "absolute",
              zIndex: 9999,
              opacity: 1,
              background: "#ffffff4d"
            }}
          >
            <div>{spinnerTable}</div>
          </div>
        )}
        <div>
          <Table
            {...getTableProps()}
            className={`table ${isStriped && "table-striped"
              } table-hover sticky`}
          >
            {customHeaderGroups !== null && (
              <>
                <thead>
                  {customHeaderGroups.map((headerGroup, key) => (
                    <tr
                      {...headerGroup.getHeaderGroupProps()}
                      style={{ verticalAlign: "middle" }}
                      key={key}
                    >
                      {headerGroup.headers.map((column, idx) => (
                        <th
                          {...column.getHeaderProps(
                            column.getSortByToggleProps()
                          )}
                          onClick={i => handleChangeTable("sort", column)}
                          key={idx}
                          colSpan={
                            column.columns?.length > 1
                              ? column.columns.length
                              : undefined
                          }
                          rowSpan={column.columns?.length > 1 ? undefined : 2}
                          style={column.style}
                          className={`${firstIsSticky && idx === 0 ? "stickyHead" : ""
                            } ${column.nowrap ? "text-nowrap" : ""}`}
                        >
                          <span className="header-span-text">
                            {column.render("Header")}
                          </span>
                          <span>
                            {tableSortType?.ordering != "none" ? (
                              tableSortType?.ordering == "asc" ? (
                                <i className="box bx bx-caret-down align-middle" />
                              ) : (
                                <i className="box bx bx-caret-up align-middle" />
                              )
                            ) : (
                              ""
                            )}
                            {/* {column.isSorted ? (
                              column.isSortedDesc ? (
                                <i className="box bx bx-caret-down align-middle" />
                              ) : (
                                <i className="box bx bx-caret-up align-middle" />
                              )
                            ) : (
                              ""
                            )} */}
                          </span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row, i) => {
                    prepareRow(row);
                    const isCashbackPositive = row.original?.custom_cashback > 0;

                    return (
                      <tr {...row.getRowProps()} key={i}>
                        {row.cells.map((cell, key) => {
                          return (
                            <td
                              {...cell.getCellProps([
                                {
                                  style: cell.column.style,
                                },
                                getCellProps(cell),
                              ])}
                              key={key}
                              colSpan={
                                firstCellIsMultiple
                                  ? mergeCell(i, key, "col", cell) || undefined
                                  : undefined
                              }
                              // colSpan={i == 0 && key == 2 && 3}
                              className={`${firstIsSticky && key === 0 ? "stickyBody" : ""
                                } align-middle ${firstCellIsMultiple &&
                                mergeCell(i, key, "display")
                                } ${customColorsCashback && cell.column.accessor_id === 'cashback' && isCashbackPositive ? `custom-bg` : ``}`}
                            // style={{
                            //   ...cell.column.style,
                            //   color:
                            //     cell.value === "N/A"
                            //       ? "rgba(73,80, 87, 0.6)"
                            //       : "rgba(73,80, 87, 1)",
                            // }}
                            >
                              {cell.render("Cell")}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
                {withFooter && (
                  <tfoot>
                    {footerGroups.map((group, key) => (
                      <tr
                        key={key}
                        {...group.getFooterGroupProps()}
                        style={{ fontWeight: "bold", background: '#fff', color: '#000', borderTop: '1px solid #eff2f7' }}
                      >
                        {group.headers.map((column, idx) => (
                          <td
                            key={idx}
                            {...column.getFooterProps()}
                            style={{ textAlign: "center" }}
                          >
                            {column.render("Footer")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tfoot>
                )}
              </>
            )}
          </Table>
        </div>
      </Styles>
      {(data?.results?.length <= 0 || data?.length <= 0) && (
        <div
          className={` ${loading ? "d-none" : "d-flex"
            } d-flex align-items-center justify-content-center spin-loader`}
          style={{
            height: "50px",
            fontWeight: 500,
          }}
        > 
          {childNoData ? childNoData : <div>There is no data to display</div>}
        </div>
      )}
      {/* {!loading ? (
                    <div
          className={` ${
            loading ? "d-none" : "d-flex"
          } d-flex align-items-center justify-content-center spin-loader`}
          style={{
            height: "50px",
            fontWeight: 500,
          }}
        >
          <div>There is no data to display</div>
        </div> */}
      {pagination && data?.results?.length > 0 && (
        <Row className="align-items-md-center mt-30">
          <Col className="inner-custom-pagination d-flex">
            <div className="d-inline">
              <SizePerPageDropdown
                sizePerPage={data.sizePerPage || data.size}
                onChangeSize={handleChangeTable}
              />
            </div>

            <div className="text-md-right ms-auto">
              <div style={{ marginTop: "10px" }}>
                <PaginationListStandalone {...paginationSettings} />
              </div>
            </div>
          </Col>
        </Row>
      )}
    </React.Fragment>
  );
};

const Styles = styled.div`
  min-width: 300px;
  .spin-loader {
    z-index: 2;
    background: rgba(255, 255, 255, 0.5);
    top: 0;
    bottom: 20px;
  }

  table {
    width: 100%;
    border-spacing: 0;
    border: 1px solid #eff2f7;
    --bs-table-hover-bg: rgba(229, 229, 229, 0.7);
    position: relative;

    .stickyHead {
      position: sticky !important;
      left: -1px;
      top: 0;
      z-index: 1;
      background-color: #2a3042;
    }

    .stickyBody {
      position: sticky !important;
      left: -1px;
      top: 0;
      z-index: 0;
      background-color: white;
    }

    .loader-for-react-table {
      position: absolute;
      left: 0;
      width: 100%;
      height: 100%;
      right: 0;
      bottom: 0;
      top: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba(255, 255, 255, 0.3);
      z-index: 1;
    }

    thead {
      background: #2a3042;
      color: white;
    }

    tfoot {
      background: rgba(229, 229, 229, 0.7);
      color: white;
    }

    tr {
      .custom-bg {
          background-color: #FFFEE2; /* Замените цвет на желаемый */
      }
      :last-child {
        td {
          border-bottom: 0;
        }
      }

      &:hover {
        .add-table-new-row {
          -webkit-transition: all 0.5s ease;
          -moz-transition: all 0.5s ease;
          transition: all 0.5s ease;
          opacity: 1;
          display: flex;
        }
      }
    }

    th {
      text-align: center;
      font-family: var(--bs-body-font-family);
      font-size: var(--bs-body-font-size);
      font-weight: 600;
      cursor: pointer;
      .header-span-text {
        cursor: pointer;
      }
    }

    th,
    td {
      margin: 0;
      padding: 0.5rem;
      border-bottom: 1px solid #eff2f7;
      border-right: 1px solid #eff2f7;
    }

    td {
      transition-duration: 0.5s;
      text-align: left;
    }
  }
`;

ReactTableForm.defaultProps = {
  firstCellIsMultiple: false,
  columns: [
    {
      Header: "",
      accessor: "name",
      isSorted: true,
    },
  ],
  data: {
    results: [
      {
        id: 1,
        name: "...",
      },
    ],
  },
  firstIsSticky: false,
  isSearch: false,
  withFooter: false,
  customColorsCashback: false,
};

ReactTableForm.propTypes = {
  data: PropTypes.any,
  columns: PropTypes.array,
  onTableChange: PropTypes.func,
  loading: PropTypes.bool,
  pagination: PropTypes.bool,
  firstIsSticky: PropTypes.bool,
  isSearch: PropTypes.bool,
  withFooter: PropTypes.bool,
  getCellProps: PropTypes.func,
  isStriped: PropTypes.bool,
  overflow: PropTypes.bool,
  firstCellIsMultiple: PropTypes.bool,
  className: PropTypes.string,
  childNoData: PropTypes.any,
  customColorsCashback: PropTypes.bool,
};

export default ReactTableForm;